/**
 * The session-detail path builder.
 *
 * Split out of `src/features/sessions/utils/buildSessionPath.ts`, and the split
 * is the whole point: the mapping here is a pure function of a session id and
 * one boolean, while the hook that stayed behind reads
 * `useIsNewSessionListEnabled()` — a flag, which is app-resolved by rule and
 * cannot come behind a door.
 *
 * So the FLAG crosses as an argument and the DECISION lives here. That is the
 * shape `@heidi/agents/contracts`' chat-home-path builders already use, and it
 * is the reason a consumer that needs the path no longer has to reach into
 * `src/features/sessions` for it: it resolves the flag where it already
 * resolves flags and asks this module which route that means.
 *
 * BOTH ROUTES ARE LIVE. The legacy page is not deprecated in any sense a reader
 * could infer from the flag's name — a user without the new list lands on it.
 */

/** The new session-list detail route, reached when the flag is on. */
export const NEW_SESSION_LIST_PATH_PREFIX = '/scribe/sessions';

/** The legacy session detail route, and the fallback. */
export const LEGACY_SESSION_PATH_PREFIX = '/scribe/session';

/**
 * `/scribe/sessions/:id` when the new session list is enabled,
 * `/scribe/session/:id` otherwise.
 *
 * The singular/plural difference is the entire mapping and it is one character
 * wide, which is exactly why it belongs in one place rather than in a ternary
 * at every call site.
 */
export const buildSessionPath = (
  sessionId: string,
  isNewSessionListEnabled: boolean
): string =>
  isNewSessionListEnabled
    ? `${NEW_SESSION_LIST_PATH_PREFIX}/${sessionId}`
    : `${LEGACY_SESSION_PATH_PREFIX}/${sessionId}`;

/**
 * The two Next route PATTERNS that render a session detail page — the other
 * half of the same contract as `buildSessionPath`, which is why it lives here
 * rather than beside any one of the three call sites that used to keep its own
 * copy.
 *
 * These are `router.pathname` values, not URLs: Next reports the file-system
 * route for the matched page, so the id, the query string and any locale prefix
 * have already been factored out. That is what makes the list finite and exact.
 */
export const LEGACY_SESSION_DETAIL_ROUTE = `${LEGACY_SESSION_PATH_PREFIX}/[...id]`;

/**
 * Note the OPTIONAL catch-all: this one pattern serves both `/scribe/sessions`
 * (the list, nothing selected) and `/scribe/sessions/:id`. A caller that needs
 * "a session is open" cannot get it from the pattern alone.
 */
export const NEW_SESSION_LIST_DETAIL_ROUTE = `${NEW_SESSION_LIST_PATH_PREFIX}/[[...id]]`;

export const SESSION_DETAIL_ROUTE_PATTERNS = [
  LEGACY_SESSION_DETAIL_ROUTE,
  NEW_SESSION_LIST_DETAIL_ROUTE,
] as const;

const SESSION_DETAIL_ROUTE_PATTERN_SET: ReadonlySet<string> = new Set(
  SESSION_DETAIL_ROUTE_PATTERNS
);

/**
 * Whether a `router.pathname` is one of the session detail pages.
 *
 * Takes the ROUTE PATTERN, never a resolved path — `router.pathname`, not
 * `router.asPath`. Handing it a real URL returns `false`, which the tests pin,
 * because a silently-false predicate is a dead code path rather than a bug you
 * can see.
 *
 * Deliberately NOT a `startsWith(LEGACY_SESSION_PATH_PREFIX)` check: that also
 * claims `/scribe/session` (the legacy index, which only redirects) and
 * `/scribe/sessions` (the list). Both prefixes are proper prefixes of a real
 * route, so a prefix test cannot separate "on a session" from "near one".
 *
 * NOT ENOUGH TO GATE SESSION UI. The new route is an OPTIONAL catch-all, so
 * `/scribe/sessions/[[...id]]` is equally the pattern for the bare list with
 * nothing selected — this returns `true` there. Anything that means "a session
 * is open" wants `isOpenSessionRoute`; this one answers only "which page am I
 * on", which is what a page-level loader or sentinel needs.
 */
export const isSessionDetailRoute = (pathname: string | undefined | null): boolean =>
  pathname != null && SESSION_DETAIL_ROUTE_PATTERN_SET.has(pathname);

/**
 * Whether the current route is a session detail page **with a session open**.
 *
 * The distinction is not pedantic. `/scribe/sessions` and `/scribe/sessions/abc`
 * are one Next route — there is no `pages/scribe/sessions/index.tsx`, so the
 * optional catch-all serves both and reports the same `router.pathname` for
 * each. The route id is the only thing that separates a list from an open
 * session, so a guard built on the pathname alone fires on the empty list.
 *
 * On the legacy route the id is implicit (a REQUIRED catch-all cannot match
 * without one), which is why the exact `pathname === '/scribe/session/[...id]'`
 * checks this replaced never had to ask for it.
 *
 * `routeSessionId` is the normalised route id — `router.query.id` reduced to a
 * string, or whatever the caller already has. Callers must keep it in their
 * effect dependencies: `router.query` is empty until `router.isReady`, so a
 * guard that reads it without depending on it settles on the pre-hydration
 * answer.
 *
 * The `typeof` test rather than a bare truthiness check is deliberate: it is
 * what the paragraph above promises, so the runtime and the doc say the same
 * thing. `''`, `null` and `undefined` — the three shapes an unhydrated or
 * absent route id actually takes — all read as "no session open".
 */
export const isOpenSessionRoute = (
  pathname: string | undefined | null,
  routeSessionId: string | undefined | null
): boolean =>
  isSessionDetailRoute(pathname) &&
  typeof routeSessionId === 'string' &&
  routeSessionId.length > 0;

/**
 * Whether a `router.pathname` is the LEGACY session detail page specifically.
 *
 * A different question from `isSessionDetailRoute`: the create-session flows ask
 * it to decide whether a user who is already sitting on the legacy page should
 * be kept there, rather than which route a fresh session belongs on.
 *
 * An exact pattern match, the same shape as `isSessionDetailRoute`, so the two
 * sibling predicates answer the same way for the same class of input. The three
 * call sites this replaced each ran
 * `router.pathname.startsWith('/scribe/session/')`, and that prefix test and
 * this exact one are the same function over every input they can produce:
 * `src/pages/scribe/session/` holds exactly `index.tsx` and `[...id].tsx`, so
 * `/scribe/session/[...id]` is the only `router.pathname` that can start with
 * `/scribe/session/`. The prefix form only differed on resolved URLs, which no
 * caller passes and which the sibling predicate rejects.
 *
 * The distinction it does have to keep making is singular vs plural.
 * `/scribe/session` is a proper prefix of `/scribe/sessions`, and matching the
 * full pattern keeps the new route out — the one-character difference that
 * `buildSessionPath` exists to contain.
 */
export const isLegacySessionDetailRoute = (
  pathname: string | undefined | null
): boolean => pathname === LEGACY_SESSION_DETAIL_ROUTE;
